import React from 'react';

// =========================================================================
// SKELETON LOADER CORE COMPONENTS
// =========================================================================

/**
 * Helper component to render a single skeleton block element.
 * @param {string} width - The CSS width property (e.g., '100px', '80%').
 * @param {string} height - The CSS height property (e.g., '1rem', '20px').
 * @param {string} className - Additional CSS class names.
 */
function SkeletonBlock({ width = '100%', height = '1rem', className = '' }) {
  return <div
        className={`skeleton-block ${className}`}
        style={{ width, height }}
     />
}

/**
 * Renders a single row of the skeleton grid, mimicking the data column structure.
 * We use the custom grid classes here and provide placeholder widths for the blocks.
 */
function SkeletonRow() {
  return <div className="cutomRow">
        {/* Column 1: SL (5%) */}
        <div className="cutomRowData"><SkeletonBlock width="25px" /></div>
        {/* Column 2: Order ID (8%) */}
        <div className="cutomRowData"><SkeletonBlock width="80px" /></div>
        {/* Column 3: Name (14%) */}
        <div className="cutomRowData"><SkeletonBlock width="130px" /></div>
        {/* Column 4: Phone (11%) */}
        <div className="cutomRowData"><SkeletonBlock width="100px" /></div>
        {/* Column 5: Address (20%) */}
        <div className="cutomRowData"><SkeletonBlock width="85%" /></div>
        {/* Column 6: Note (12%) */}
        <div className="cutomRowData"><SkeletonBlock width="70px" /></div>
        {/* Column 7: Product (15%) */}
        <div className="cutomRowData"><SkeletonBlock width="100px" /></div>
        {/* Column 8: Status (auto) - Contains the select box placeholder */}
        <div className="cutomRowData"><SkeletonBlock width="70px" height="1.6rem" /></div>
    </div>
}

/**
 * The main Skeleton Loading component, now structured using CSS Grid.
 */
function OrderListSkeleton() {
    // Generate 10 placeholder rows
    const rowCount = 12;
    const rows = Array.from({ length: rowCount }, (_, index) => (
        <SkeletonRow key={index} />
    ));

    return (
        // The style block integrates both the animation and your custom grid CSS
        <>
            <style jsx="true">{`
                /* 1. Animation Definition */
                @keyframes pulse {
                    0% { background-color: rgba(192, 192, 192, 0.4); }
                    50% { background-color: rgba(192, 192, 192, 0.7); }
                    100% { background-color: rgba(192, 192, 192, 0.4); }
                }

                /* 2. Base Skeleton Block Style */
                .skeleton-block {
                    border-radius: 4px;
                    animation: pulse 1.5s infinite ease-in-out;
                    margin-bottom: 4px;
                }

                /* 3. Grid Layout Styles (Based on user's snippet) */
                .cutomRow,
                .cutomRowHeader {
                    display: grid;
                    /* Using user's provided column layout: 8 columns */
                    grid-template-columns: 5% 8% 14% 11% 20% 12% 15% auto; 
                    grid-gap: 1px;
                    margin: 0;
                    background: rgb(255, 255, 255);
                    border-radius: 0px;
                }

                .cutomRow:nth-of-type(odd) {
                    background: #eef5fb;
                }

                .cutomHeader {
                    background: rgb(0, 5, 9);
                    color: white;
                    font-weight: bold;
                }
                
                .cutomRow:hover {
                    background: rgba(203, 203, 203, 0.597);
                    color: rgb(0, 0, 0);
                }

                .cutomRowData,
                .cutomHeader {
                    padding: 15px;
                    border-bottom: 0.5px solid rgba(0, 0, 0, 0.172);
                }
                
                /* Ensure skeleton blocks inside the row don't have bottom border */
                .cutomRowData .skeleton-block {
                    margin-bottom: 0;
                }

                /* Mobile responsiveness adaptation from user's snippet */
                @media screen and (max-width : 768px) {
                    .CustomTable2nd {
                        overflow-x: scroll;
                        padding: 5px;
                    }

                    .cutomRow,
                    .cutomRowHeader {
                        /* Fixed width for scrolling on mobile */
                        grid-template-columns: 50px 100px 150px 150px 250px 150px 200px 150px;
                        width: 1200px;
                    }
                }
                
                /* 4. Global Container Styles */
                .skeleton-container-wrapper {
                    margin-top :15px;
                    border: 1px solid #e0e0e0;
                    border-radius: 12px;
                    background-color: white;
                    box-shadow: 0 6px 15px rgba(0, 0, 0, 0.08);
                    width: 100%;
                    
                    font-family: 'Inter', sans-serif;
                }

                /* 5. Pagination Footer Styles */
                .skeleton-pagination {
                    display: flex;
                    justify-content: flex-end;
                    padding-top: 20px;
                    margin-top: 20px;
                    border-top: 1px solid #eee;
                }
                
                .skeleton-pagination .skeleton-block {
                    margin-right: 15px;
                    margin-bottom: 0;
                }
            `}</style>

            <div className="skeleton-container-wrapper">
                {/* Header structure is explicitly included to ensure the grid template is set */}
                {/* <div className="cutomRowHeader">
                    <div className="cutomHeader">SL</div>
                    <div className="cutomHeader">Order Id</div>
                    <div className="cutomHeader">Name</div>
                    <div className="cutomHeader">Phone</div>
                    <div className="cutomHeader">Address</div>
                    <div className="cutomHeader">Note</div>
                    <div className="cutomHeader">Product</div>
                    <div className="cutomHeader">Status</div>
                </div> */}

                {/* Skeleton Rows (Table Body equivalent) */}
                <div className="table-body-rows">
                    {rows}
                </div>

                {/* Skeleton for Pagination/Footer */}
                {/* <div className="skeleton-pagination">
                    <SkeletonBlock width="120px" height="32px" />
                    <SkeletonBlock width="60px" height="32px" />
                </div> */}
            </div>
        </>
    );
}

// =========================================================================
// DEMO WRAPPER (App) - For Demonstration Only
// =========================================================================

// Wrapping App component to demonstrate the loading state transition
function OrderListSkeletonApp() {
    

    return (
        <div style={{ padding: '0px', backgroundColor: '#f9fafb', minHeight: '85vh', display: 'flex', justifyContent: 'center', }}>
            {/* The user's external title, simulated here */}
                <OrderListSkeleton />
        </div>
    );
}

export default OrderListSkeletonApp;